import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { get, some, each, forEach, chain, map, pick } from 'lodash';
import { Subject, Unsubscribable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import {
  DateFormatterService,
  PaginatorComponent,
  UserAuth,
  UserService,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { UserAdministrationService } from '../../services/user.administration.service';
import { ControllerApiService } from '@nrwl/test-setup';
export interface IWidgetPayloadRequest {
  dimension?: string;
  format?: string;
  fromTime?: number;
  toTime?: number;
}

@Component({
  selector: 'nrwl-user-administration',
  templateUrl: './user-administration.component.html',
  styleUrls: ['./user-administration.component.scss']
})
export class UserAdministrationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('paginator', { static: true }) paginatorComponent: PaginatorComponent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  @ViewChild('usersTable') usersTable: MatTable<any>;
  @ViewChild('uSort') uSort: MatSort;
  @ViewChild('gSort') gSort: MatSort;
  @ViewChild('rSort') rSort: MatSort;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  userAdministrationBoard: any = {};
  searchText: string;
  addMenuVisible = false;
  actionsVisible = false;
  isLoading = true;
  showPagination = true;
  updateSubscriptions: Unsubscribable[] = [];

  //TODO - add pagination for every table
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  usersData: MatTableDataSource<any>;
  columnsUsers: string[] = ['userId', 'users', 'groups', 'lastLogin', 'registration', 'actions'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  groupsData: MatTableDataSource<any>;
  columnsGroups: string[] = ['name', 'roles', 'count'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  rolesData: MatTableDataSource<any>;
  columnsRoles: string[] = ['name', 'description'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  userInfo: any;
  queryUserName: string;
  defaultRoleId = 'default_role';
  tabIndex = 0;
  searchValue = '';
  searchTextPerTab = ['', '', ''];
  navigateIMSUrl = false;
  showAlertsPreference: boolean;
  showThemePreference: boolean;
  hasNumUserLicense = false;
  outletParams: boolean;
  //eslint-disable-next-line
  environment: any;
  isFusionUIProject = false;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly userAdministrationService: UserAdministrationService,
    private readonly userService: UserService,
    readonly userAuth: UserAuth,
    private readonly dateFormatterService: DateFormatterService,
    private readonly navigator: NavigationProvider,
    private readonly valuesExchange: ValuesExchangeProvider,
    private readonly activatedRoute: ActivatedRoute,
    public controllerApiService: ControllerApiService
  ) {}

  ngOnInit() {
    this.environment = this.userService.getEnvironment();
    this.isFusionUIProject = this.userService.isFusionUIProject();
    this.userService.updateNciToken();
    this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
      this.showAlertsPreference = params.showAlertsPreference === 'true' ? true : false;
      this.showThemePreference = params.showThemePreference === 'true' ? true : false;
      this.outletParams = params.outletParams === 'true' ? true : false;
    });
    const navigateIMSUrl = sessionStorage.getItem('navigateIMSUrl');
    this._setNavigateIMSUrl(navigateIMSUrl);
    this.isLicenseAvailable();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
    this.usersData = new MatTableDataSource<any>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
    this.groupsData = new MatTableDataSource<any>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
    this.rolesData = new MatTableDataSource<any>([]);
    this._getAllTableData$();
    this.updateSubscriptions.push(
      this.valuesExchange.registerInterest('user_admin_board', () => {
        this._getAllTableData$();
      })
    );
    this.valuesExchange.notifyListeners('user_admin_board', {
      value: 'update',
      cacheBreaker: Date.now()
    });
  }

  ngAfterViewInit(): void {
    this.usersData.filterPredicate = this.filterUsers.bind(this);
    this.groupsData.filterPredicate = this.filterGroups.bind(this);
    this.rolesData.filterPredicate = this.filterRoles.bind(this);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  tabChanged(event): void {
    this.tabIndex = event.index;
    this.searchValue = this.searchTextPerTab[this.tabIndex];
    this.filterDataByText(this.searchValue);
    if (event.tab.textLabel === 'Users') {
      this.showPagination = true;
    } else {
      this.showPagination = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  filterUsers(row: any, filterText: string): boolean {
    return some(row, (value, key) => {
      switch (key) {
        case 'username':
          return this._getJoinedValues$(row, ['username']).toLowerCase().indexOf(filterText) > -1;
        case 'name':
        case 'surname':
          return (
            this._getJoinedValues$(row, ['name', 'surname']).toLowerCase().indexOf(filterText) > -1
          );

        case 'groups':
          // eslint-disable-next-line @typescript-eslint/no-explicit-any,no-case-declarations,@typescript-eslint/explicit-module-boundary-types
          const obj = chain(value).mapValues('name').value();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any,no-case-declarations,@typescript-eslint/explicit-module-boundary-types
          const keys = Object.keys(obj);
          return this._getJoinedValues$(obj, keys).toLowerCase().indexOf(filterText) > -1;
        case 'registerDate':
        case 'lastLogin':
          return (
            value &&
            this.dateFormatterService.getDateTime(value).toLowerCase().indexOf(filterText) > -1
          );
        default:
          return false;
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  filterGroups(row: any, filterText: string): boolean {
    return (
      row.name.toLowerCase().indexOf(filterText) > -1 ||
      this.groupUsersCount(row.name).toString().indexOf(filterText) > -1
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  filterRoles(row: any, filterText: string): boolean {
    return (
      this.getRoleFriendlyName(row.name).toLowerCase().indexOf(filterText) > -1 ||
      this.getRoleDescription(row.description).toLowerCase().indexOf(filterText) > -1
    );
  }

  setSort(): void {
    this.usersData.sort = this.uSort;
    this.usersData.sort.disableClear = true;
    this.usersData.sortingDataAccessor = this.userSortingDataAccessor.bind(this);

    this.groupsData.sort = this.gSort;
    this.groupsData.sort.disableClear = true;
    this.groupsData.sortingDataAccessor = this.groupsSortingDataAccessor.bind(this);

    this.rolesData.sort = this.rSort;
    this.rolesData.sort.disableClear = true;
    this.rolesData.sortingDataAccessor = this.rolesSortingDataAccessor.bind(this);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  userSortingDataAccessor(data: any, sortHeaderId: string) {
    switch (sortHeaderId) {
      case 'userId':
        return data?.username.toLowerCase();
      case 'users':
        return `${data?.name.toLowerCase()} ${data?.surname.toLowerCase()}`;
      case 'groups':
        return map(data.groups, group => group.name.toLowerCase()).join(' ');
      case 'roles':
        return map(data.effectiveRoles, role => role.name.toLowerCase()).join(' ');
      case 'lastLogin':
        return get(data, 'lastLoginDate.timestamp');
      case 'registration':
        return get(data, 'registerDate.timestamp');
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  groupsSortingDataAccessor(data: any, sortHeaderId: string) {
    switch (sortHeaderId) {
      case 'name':
        return data.name.toLowerCase();
      case 'count':
        return this.groupUsersCount(data.name);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  rolesSortingDataAccessor(data: any, sortHeaderId: string) {
    switch (sortHeaderId) {
      case 'name':
        return this.getRoleFriendlyName(data.name).toLowerCase();
      case 'description':
        return this.getRoleDescription(data.description).toLowerCase();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  formatDates(data): void {
    data.forEach(item => {
      item.lastLoginDate = {
        date: item.lastLogin ? this.dateFormatterService.getLocaleDateString(item.lastLogin) : '',
        time: item.lastLogin ? this.dateFormatterService.getTime(item.lastLogin) : '',
        timestamp: item.lastLogin
      };
      item.registerDate = {
        date: this.dateFormatterService.getLocaleDateString(item.createdAt),
        time: this.dateFormatterService.getTime(item.createdAt),
        timestamp: item.createdAt
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  removeUsersRoles(usersData): void {
    forEach(usersData, user => {
      this.userAuth.removeRoles(user.effectiveRoles);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  removeGroupsRoles(groupsData): void {
    forEach(groupsData, group => {
      this.userAuth.removeRoles(group.roles);
    });
  }

  onSearchKeywordChange(text: string): void {
    text = text.toLowerCase();
    this.searchTextPerTab[this.tabIndex] = text;
    this.filterDataByText(text);

    if (this.usersData.paginator) {
      this.usersData.paginator.firstPage();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  filterDataByText(text): void {
    switch (this.tabIndex) {
      case 0:
        this.usersData.filter = text;
        if (this.usersData.paginator) {
          this.usersData.paginator.firstPage();
        }
        break;
      case 1:
        this.groupsData.filter = text;
        break;
      case 2:
        this.rolesData.filter = text;
        break;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  navigateToWizard(wizardId, defaultParams = {}): void {
    this.navigator.navigateToWizard([wizardId], {
      queryParams: {
        defaultParams: JSON.stringify(defaultParams)
      }
    });
  }

  groupUsersCount(groupName: string): number {
    let userNo = 0;
    if (this.usersData.data) {
      this.usersData.data.forEach(user => {
        user.groups.forEach(group => {
          if (group.name === groupName) userNo++;
        });
      });
    }
    return userNo;
  }

  roleUsersCount(roleName: string): number {
    let userNo = 0;
    if (this.usersData.data) {
      this.usersData.data.forEach(user => {
        user.effectiveRoles.forEach(role => {
          if (role.name === roleName) userNo++;
        });
      });
    }
    return userNo;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getRoleDescription(description: string): string {
    return this.userAuth.getRoleDescriptionName(description);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getRoleFriendlyName(name: string): string {
    return this.userAuth.getRoleFriendlyName(name);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get readOnly() {
    return this.userAuth.readOnly;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  canManagePermissions() {
    return this.userAuth.isAuthedFor('MANAGE_PERMISSIONS');
  }
  search(): void {
    //TODO - with a specific search service or directly calling the backend API?
  }

  openAddMenu(): void {
    this.addMenuVisible = true;
  }

  closeAddMenu(): void {
    this.addMenuVisible = false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars,@typescript-eslint/explicit-module-boundary-types
  openActionsMenu(user: any): void {
    this.actionsVisible = true;
  }

  closeActionsMenu(): void {
    this.actionsVisible = false;
  }

  createUser(): void {
    this.closeAddMenu();
  }

  createGroup(): void {
    this.navigateToWizard('new_user_group');
    this.closeAddMenu();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  goToProfile(row: any): void {
    const dashboardOutletParams = this.outletParams ? ['profile', row.username] : 'profile';
    const queryParams = {
      showThemePreference:
        this.userAuth.getUserAuth()?.data?.USER_ID === row.username
          ? this.showThemePreference
          : false,
      showAlertsPreference: this.showAlertsPreference,
      uid: row.id
    };
    if (!this.outletParams) {
      queryParams['userName'] = row.username;
    }
    this.navigator.navigateToDashboard(dashboardOutletParams, {
      queryParams: queryParams
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  addToNewGroup(user: any): void {
    const defaultParams = {
      userNames: [user]
    };
    this.navigateToWizard('new_user_group', defaultParams);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  addUserToGroup(user: any): void {
    const defaultParams = {
      userNames: [user.username],
      add_remove_group: 'add_remove_group',
      groupIds: user.groups,
      id: user.id
    };
    this.navigateToWizard('fusion_sso_add_user_to_groups', defaultParams);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  removeUserFromGroup(user: any): void {
    const defaultParams = {
      userNames: [user.username],
      groupIds: user.groups,
      add_remove_group: 'add_remove_group',
      id: user.id
    };
    this.navigateToWizard('fusion_sso_remove_user_from_group', defaultParams);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
  openUpdateUserWizard(wizardId: string, user: any): void {
    const userInfo = pick(user, ['email', 'name', 'surname', 'id']);
    this.navigateToWizard(wizardId, userInfo);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  disableUser(user: any): void {
    this.userAdministrationService
      .disableUser$(user.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        user.active = false;
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  enableUser(user: any): void {
    this.userAdministrationService
      .enableUser$(user.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        user.active = true;
      });
  }

  /*END - Actions*/

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    for (const rec of this.updateSubscriptions) {
      if (rec) {
        rec.unsubscribe();
      }
    }
  }

  nextPage(event: PageEvent): void {
    this.usersData.paginator.pageIndex = event.pageIndex;
  }

  navigateToSSOApp(): void {
    if (this.controllerApiService.serverConfig?.['vibe-identity']) {
      const endURL = this.controllerApiService.serverConfig?.['vibe-identity']?.['ssoURL'];
      const url = this.environment.production
        ? endURL
        : this.environment.serviceHostName
        ? this.environment.serviceProtocol + '://' + this.environment.serviceHostName + endURL
        : `https://${this.environment.EnvKeys.CallHomeUrl}` + endURL;
      window.open(url);
    }
  }

  navigateToIMSConfig(): void {
    const endURL = '/ims/config-ui/login';
    const url = this.environment.production
      ? endURL
      : this.environment.serviceHostName
      ? this.environment.serviceProtocol + '://' + this.environment.serviceHostName + endURL
      : `https://${this.environment.EnvKeys.CallHomeUrl}` + endURL;
    window.open(url);
  }

  private _getJoinedValues$(item, keys) {
    const arrayValues = [];

    each(keys, key => {
      arrayValues.push(get(item, key, ''));
    });

    return arrayValues.join(' ');
  }

  private _getAllTableData$(): void {
    this._getAllUsers$();
    this._getAllGroups$();
    this._getAllRoles$();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _getAllUsers$(): void {
    this.userAdministrationService
      .getUsers$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
        response => {
          const usersData = response.success;
          this.removeUsersRoles(usersData);
          this.formatDates(usersData);
          usersData.forEach(item => {
            item.name = item.name ? item.name : '';
            item.surname = item.surname ? item.surname : '';
          });
          this.usersData.data = usersData;
          this.setSort();
          if (this.paginatorComponent && this.paginatorComponent.paginator) {
            this.usersData.paginator = this.paginatorComponent.paginator;
          }
          this._setGravatarForUsers$(this.usersData.data);
          this.isLoading = false;
        }
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  private _setGravatarForUsers$(users: any) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
    users.forEach((u: any) => {
      u.gravatarUrl = this.userService.getGravatarUrl(u.email);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  private _getAllGroups$(): void {
    this.userAdministrationService
      .getGroups$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(result => {
        const groupsData = result.success;
        this.removeGroupsRoles(groupsData);
        this.groupsData.data = groupsData;
        sessionStorage.setItem('userUUID', result.success.find(item => item.name === 'users').id);
        this.setSort();
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unused-vars
  private _getAllRoles$(): void {
    this.userAdministrationService
      .getRoles$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(result => {
        const rolesData = result.success;
        this.userAuth.removeRoles(rolesData);
        const updatedRoles = this.userAuth.addRoles(rolesData);
        this.rolesData.data = updatedRoles?.filter(item => item.name !== 'agent');
        this.setSort();
      });
  }

  private _setNavigateIMSUrl(routeParamNavigateUrl: string): void {
    this.userAdministrationService
      .getBaseLicence$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          if (response.length > 0) {
            const hasLdapLicense = response.find(item => item.featureName === 'EAX-LDAP');
            const hasRadiusLicense = response.find(item => item.featureName === 'EAX-RADIUS');
            const hasOpenIdLicense = response.find(item => item.featureName === 'EAX-OPENID');
            if (hasLdapLicense || hasRadiusLicense || hasOpenIdLicense) {
              this.navigateIMSUrl = routeParamNavigateUrl === 'true' ? true : false;
            }
          }
        },
        () => {
          this.navigateIMSUrl = false;
        }
      );
  }

  isLicenseAvailable(): void {
    this.userAdministrationService
      .getBaseLicence$()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        if (response.length > 0) {
          this.hasNumUserLicense = response.some(item => item.featureName === 'EAX-NUM-USERS');
        }
      });
  }
}
